/**
 * Where a sign-in lands (M4S-5632).
 *
 * Every sign-in lands on Home, including one after an expired session. The
 * one exception is a person who followed a link to a specific page (an email
 * CTA, a link pasted in Slack): that page is kept as the `callbackUrl` and
 * honoured.
 *
 * The app cannot tell those two apart from the URL alone. A reopened tab from
 * yesterday and a fresh email link both arrive as "signed out, on /dani/…,
 * callbackUrl set". What differs is the tab: a tab that has already shown a
 * signed-in Studio page carries a mark in sessionStorage, and sessionStorage
 * lives as long as the tab does. It survives reloads, Chrome and Firefox
 * restore it when they reopen tabs after a restart, and a new tab opened from
 * an email or Slack starts without it. So a sign-in in a marked tab is a
 * return to old work and goes Home; a sign-in in an unmarked tab is an
 * arrival and keeps its link.
 *
 * Every storage call is wrapped: private windows and blocked site data can
 * throw, and a throw must read as "unmarked" so a link still works.
 */

import type { Session } from 'next-auth';
import { useEffect } from 'react';

import { getPostAuthLandingUrl } from '@/@studio/utils/routeAccess';

export const TAB_IN_USE_KEY = 'studio:tab-in-use';

/** Mark this tab as one that has shown a signed-in Studio page. */
export function markTabInUse(): void {
    try {
        window.sessionStorage.setItem(TAB_IN_USE_KEY, '1');
    } catch {
        // No storage: the tab stays unmarked and a later link is honoured.
    }
}

/** Whether this tab has shown a signed-in Studio page before. */
export function tabWasInUse(): boolean {
    try {
        return window.sessionStorage.getItem(TAB_IN_USE_KEY) === '1';
    } catch {
        return false;
    }
}

/**
 * Marks the tab once the signed-in layout has a live session. A session that
 * is loading, missing, or carrying an error (an expired token on the way to
 * sign-in) never marks it, so a link that lands signed out stays honoured.
 */
export function useMarkTabInUse(session: Session | null, status: string): void {
    const isSignedIn = status === 'authenticated' && !!session?.user && !session.error;
    useEffect(() => {
        if (isSignedIn) markTabInUse();
    }, [isSignedIn]);
}

/**
 * The page a sign-in sends someone to. Pure: `callbackUrl` must already be
 * validated as a same-origin path.
 */
export function resolvePostSignInTarget(
    callbackUrl: string | null,
    tabWasAlreadyInUse: boolean,
): string {
    if (callbackUrl && !tabWasAlreadyInUse) return callbackUrl;
    return getPostAuthLandingUrl();
}
